import {
    classifyNaturalSettlementCells,
    toReceiverFrameBand,
    type NaturalSettlementCell,
} from '@/adapters/primitive/_pure/model/natural/settlementCells';
import type { NaturalBandState, NaturalMachineState, NaturalTracePoint } from '@/adapters/primitive/_contract/natural/machine';
import type { PrimitiveWheelRole } from '@/adapters/primitive/_contract/operation';

export type NaturalSettlementIncomingKind = 'contribution-traveller' | 'radix-band' | 'unit' | 'assembly-member';

export type NaturalSettlementBandCells = {
    incomingKind: NaturalSettlementIncomingKind;
    /** Power of the incoming band; equals the receiver power except when RadixBand settles onto a Contribution-traveller. */
    power: number;
    cells: readonly NaturalSettlementCell[];
};

function emptyBand(power: number): NaturalBandState {
    return { power, countedSlots: [], overhangSlots: [], marker: null };
}

function bandIn(state: NaturalMachineState, role: PrimitiveWheelRole, power: number): NaturalBandState {
    return state.wheels
        .find((wheel) => wheel.role === role)
        ?.bands.find((band) => band.power === power)
        ?? emptyBand(power);
}

function settlementOnto(args: {
    state: NaturalMachineState;
    role: PrimitiveWheelRole;
    incomingKind: NaturalSettlementIncomingKind;
    incoming: NaturalBandState;
    slotOffset: number;
    receiver?: NaturalBandState;
}): NaturalSettlementBandCells {
    return {
        incomingKind: args.incomingKind,
        power: args.incoming.power,
        cells: classifyNaturalSettlementCells({
            radix: args.state.radix,
            receiver: args.receiver ?? bandIn(args.state, args.role, args.incoming.power),
            incoming: args.incoming,
            slotOffset: args.slotOffset,
        }),
    };
}

/** Per-cell outcomes of the settlement a trace point performs; empty when the point settles nothing. */
export function resolveNaturalSettlementCells(point: NaturalTracePoint): NaturalSettlementBandCells[] {
    const state = point.previousState;
    const event = point.event.event;
    switch (event.eventKind) {
        case 'contribution-traveller-settled-on-result':
            return state.contributionTraveller === null ? [] : [settlementOnto({
                state,
                role: event.receiverInteraction.receiver.wheelRole,
                incomingKind: 'contribution-traveller',
                incoming: state.contributionTraveller.band,
                slotOffset: state.contributionTraveller.slotOffset,
            })];
        case 'deficit-discharged':
            return state.contributionTraveller === null || state.assembly === null ? [] : [settlementOnto({
                state,
                role: state.contributionTraveller.sourceRole,
                incomingKind: 'contribution-traveller',
                incoming: state.contributionTraveller.band,
                slotOffset: state.contributionTraveller.slotOffset,
                receiver: state.assembly.nextBand,
            })];
        case 'assembly-settled-on-operand': {
            const radixBand = state.assembly?.radixBand;
            if (state.contributionTraveller === null || radixBand === null || radixBand === undefined) return [];
            return [settlementOnto({
                state,
                role: state.contributionTraveller.sourceRole,
                incomingKind: 'radix-band',
                incoming: radixBand,
                slotOffset: 0,
                receiver: toReceiverFrameBand({
                    band: state.contributionTraveller.band,
                    slotOffset: state.contributionTraveller.slotOffset,
                    radix: state.radix,
                }),
            })];
        }
        case 'composite-formed': {
            const assembly = state.assembly;
            if (assembly?.assemblyRole !== 'carry' || assembly.radixBand === null) return [];
            const merged = state.composite?.outerUnit
                ?? (state.contributionTraveller === null ? null : { band: state.contributionTraveller.band, slotOffset: state.contributionTraveller.slotOffset });
            if (merged === null) return [];
            const role = event.receiverInteraction.receiver.wheelRole;
            return [
                settlementOnto({
                    state,
                    role,
                    incomingKind: 'radix-band',
                    incoming: assembly.radixBand,
                    slotOffset: 0,
                    receiver: toReceiverFrameBand({ band: merged.band, slotOffset: merged.slotOffset, radix: state.radix }),
                }),
                // NextBand joins as the assembly's outer Unit, landing on the empty band above the merged one.
                settlementOnto({
                    state,
                    role,
                    incomingKind: 'unit',
                    incoming: assembly.nextBand,
                    slotOffset: 0,
                    receiver: emptyBand(assembly.nextBand.power),
                }),
            ];
        }
        case 'composite-settled': {
            const assembly = state.composite;
            if (assembly === null) return [];
            return [...assembly.lowerBands, assembly.outerUnit].map((member) => settlementOnto({
                state,
                role: event.receiverInteraction.receiver.wheelRole,
                incomingKind: 'assembly-member',
                incoming: member.band,
                slotOffset: member.slotOffset,
            }));
        }
        case 'receiver-bands-settled': {
            const role = event.receiverInteraction.receiver.wheelRole;
            const assembly = state.multiplication?.borrowAssembly ?? null;
            if (assembly !== null) {
                return [
                    ...assembly.lowerBands.map((member) => settlementOnto({
                        state,
                        role,
                        incomingKind: 'assembly-member',
                        incoming: member.band,
                        slotOffset: 0,
                    })),
                    settlementOnto({
                        state,
                        role,
                        incomingKind: 'assembly-member',
                        incoming: assembly.outerUnit.band,
                        slotOffset: assembly.outerUnit.slotOffset,
                    }),
                ];
            }
            const unit = state.multiplication?.unit ?? null;
            return unit === null ? [] : [settlementOnto({
                state,
                role,
                incomingKind: 'unit',
                incoming: unit.band,
                slotOffset: unit.slotOffset,
            })];
        }
        default:
            return [];
    }
}
